import LoadingStates from "./components/LoadingStates";
import ErrorStates from "./components/ErrorStates";
import MainLayout from "./components/MainLayout";
import LoggingOutSpinner from "./LoggingOutSpinner";

// Custom hooks
import { useAuthHandler } from "./hooks/useAuthHandler";
import { useCustomerSiteManager } from "./hooks/useCustomerSiteManager";
import { useTimezoneHandler } from "./hooks/useTimezoneHandler";
import { useFactoryDashboard } from "./hooks/useFactoryDashboard";
import { usePermissionGuard } from "./hooks/usePermissionGuard";
import { useResponsiveLayout } from "./hooks/useResponsiveLayout";
import { useOutletVisibility } from "./hooks/useOutletVisibility";
import { useComponentsManager } from "./hooks/useComponentsManager";

import "./AppContent.css";
import { Outlet, useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { setSelectedKey } from "@/redux/slices/customerSlice";
import { fetchGeneralDetails } from "@/redux/services/customerService";
import { EXECUTIVE_DASHBOARD, GENERAL } from "@/utils/constants";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "@/store";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);

export default function AppContent() {
  const dispatch = useDispatch<AppDispatch>();
  const { customer }: any = useParams();

  // Use refs to prevent unnecessary re-renders
  const previousRouteRef = useRef<string | null>(null);
  const hasDispatchedGeneralDetailsRef = useRef(false);

  // Redux state
  const {
    permittedCustomers,
    permittedCustomersLoading,
    permittedCustomersError,
    customerSites,
    selectedCustomerName,
    FactoryPath,
    ExecutivePath,
    MaintenancePath,
    selectedTimezone,
    loadingLogout,
    selectedSite,
    route,
    userInfo,
    loading,
    customerSitesLoading,
    generalDetails,
    selectedKey
  } = useSelector((state: RootState) => state.userInfo);

  const { factoryApiResponse } = useSelector((state: RootState) => state.dashboard);
  const { authLoading, loginStatus } = useAuthHandler(permittedCustomers);
  const { isMobile } = useResponsiveLayout();
  const { showOutlet } = useOutletVisibility(customerSitesLoading, customerSites);
  const { presentComponents } = useComponentsManager(customerSites, selectedSite);
  const [isNewIndustry, setIsNewIndustry] = useState(false);

  useEffect(() => {
    setIsNewIndustry((userInfo?.show_intro && location.pathname.toLowerCase().includes(`/view/${EXECUTIVE_DASHBOARD}`)) ?? false)
  }, [location.pathname, userInfo?.show_intro, selectedSite, selectedCustomerName]);

  // Initialize managers
  useCustomerSiteManager(selectedCustomerName, permittedCustomers, customerSites, selectedSite, FactoryPath, ExecutivePath);
  useTimezoneHandler(selectedTimezone);
  useFactoryDashboard(FactoryPath, ExecutivePath, factoryApiResponse);
  usePermissionGuard(
    selectedCustomerName,
    customerSitesLoading,
    permittedCustomersLoading,
    MaintenancePath,
    FactoryPath,
    ExecutivePath,
    permittedCustomers,
    customer,
    userInfo,
  );

  // Effects with proper dependency management
  useEffect(() => {
    // Only dispatch if we have the required data and haven't dispatched before
    if (
      selectedSite &&
      selectedKey === GENERAL &&
      Object.keys(generalDetails).length === 0 &&
      selectedCustomerName &&
      customerSites.length > 0 &&
      !hasDispatchedGeneralDetailsRef.current
    ) {
      hasDispatchedGeneralDetailsRef.current = true;
      dispatch(fetchGeneralDetails({
        selectedCustomerName,
        avail_sites: customerSites,
        selectedSite
      }));
    }
  }, [selectedSite, selectedKey, selectedCustomerName, customerSites.length, dispatch]);

  // Reset the ref when site changes
  useEffect(() => {
    hasDispatchedGeneralDetailsRef.current = false;
  }, [selectedSite]);

  useEffect(() => {
    // Only update if route actually changed
    if (route && route !== previousRouteRef.current) {
      previousRouteRef.current = route;
      dispatch(setSelectedKey(route));
    }
  }, [route, dispatch]);

  // Render logic
  return (
    <div className="AppContent">
      <LoadingStates
        loadingLogout={loadingLogout}
        loginStatus={loginStatus}
        customerSitesLoading={customerSitesLoading}
        FactoryPath={FactoryPath}
        permittedCustomersLoading={permittedCustomersLoading}
        permittedCustomersError={permittedCustomersError}
        authLoading={authLoading}
      />

      <ErrorStates
        loadingLogout={loadingLogout}
        loading={loading}
        loginStatus={loginStatus}
        permittedCustomersError={permittedCustomersError}
        permittedCustomers={permittedCustomers}
        permittedCustomersLoading={permittedCustomersLoading}
        authLoading={authLoading}
      />

      <MainLayout
        permittedCustomers={permittedCustomers}
        loading={loading}
        loadingLogout={loadingLogout}
        authLoading={authLoading}
        loginStatus={loginStatus}
        permittedCustomersLoading={permittedCustomersLoading}
        permittedCustomersError={permittedCustomersError}
        customerSitesLoading={customerSitesLoading}
        FactoryPath={FactoryPath}
        showOutlet={showOutlet}
        presentComponents={presentComponents}
        isMobile={isMobile}
        isNewIndustry={isNewIndustry}
      >
        <Outlet />
      </MainLayout>

      {loadingLogout && <LoggingOutSpinner />}
    </div>
  );
}